import { createSlice, PayloadAction, current } from '@reduxjs/toolkit';
import { Node, Edge } from '@xyflow/react';

export interface StageData extends Record<string, unknown> {
  id: string;
  stageName: string;
  systemPrompt: string;
  voice?: string | { name: string; value: string }; // Support both formats for backward compatibility
  temperature?: number;
  connectedStages?: {
    parentStages: { stageName: string; nodeId: string }[] | null;
    childStages: { stageName: string; nodeId: string }[] | null;
  };
  tools?: string[];
  languageHint?: string;
  initialMessage?: string;
}

export interface CallStagesFlowState {
  nodes: Node<StageData>[];
  edges: Edge[];
  selectedNode: Node<StageData> | null;
  isConfigPanelOpen: boolean;
  isSaving: boolean;
  isLoading: boolean;
  hasUnsavedChanges: boolean;
}

const initialNodes: Node<StageData>[] = [
  {
    id: 'start',
    type: 'stageNode',
    position: { x: 400, y: 100 },
    data: {
      id: 'start',
      stageName: 'Initial Stage',
      systemPrompt: 'You are a helpful assistant. How can I help you today?',
      voice: 'default',
      temperature: 0.7,
      connectedStages: {
        parentStages: [],
        childStages: [],
      },
      tools: [],
      languageHint: 'en',
      initialMessage: 'Hello! How can I help you today?',
    },
  },
];

const initialState: CallStagesFlowState = {
  nodes: initialNodes,
  edges: [],
  selectedNode: null,
  isConfigPanelOpen: false,
  isSaving: false,
  isLoading: false,
  hasUnsavedChanges: false,
};

const callStagesFlowSlice = createSlice({
  name: 'callStagesFlow',
  initialState,
  reducers: {
    // Node management
    setNodes: (state, action: PayloadAction<Node<StageData>[]>) => {
      return {
        ...state,
        nodes: action.payload,
        hasUnsavedChanges: true,
      };
    },
    addNode: (state, action: PayloadAction<Node<StageData>>) => {
      return {
        ...state,
        nodes: [...current(state.nodes), action.payload],
        hasUnsavedChanges: true,
      };
    },
    updateNode: (
      state,
      action: PayloadAction<{
        nodeId: string;
        updatedData: Partial<StageData>;
      }>
    ) => {
      const { nodeId, updatedData } = action.payload;
      const currentNodes = current(state.nodes);
      return {
        ...state,
        nodes: currentNodes.map(node =>
          node.id === nodeId
            ? { ...node, data: { ...node.data, ...updatedData } }
            : node
        ),
        hasUnsavedChanges: true,
      };
    },
    removeNode: (state, action: PayloadAction<string>) => {
      const nodeId = action.payload;
      const currentNodes = current(state.nodes);
      const currentEdges = current(state.edges);

      return {
        ...state,
        nodes: currentNodes.filter(node => node.id !== nodeId),
        edges: currentEdges.filter(
          edge => edge.source !== nodeId && edge.target !== nodeId
        ),
        selectedNode:
          state.selectedNode?.id === nodeId ? null : state.selectedNode,
        isConfigPanelOpen:
          state.selectedNode?.id === nodeId ? false : state.isConfigPanelOpen,
        hasUnsavedChanges: true,
      };
    },

    // Edge management
    setEdges: (state, action: PayloadAction<Edge[]>) => {
      return {
        ...state,
        edges: action.payload,
        hasUnsavedChanges: true,
      };
    },
    addEdge: (state, action: PayloadAction<Edge>) => {
      return {
        ...state,
        edges: [...current(state.edges), action.payload],
        hasUnsavedChanges: true,
      };
    },
    removeEdge: (state, action: PayloadAction<string>) => {
      const currentEdges = current(state.edges);
      return {
        ...state,
        edges: currentEdges.filter(edge => edge.id !== action.payload),
        hasUnsavedChanges: true,
      };
    },

    // Node selection and config panel
    setSelectedNode: (state, action: PayloadAction<Node<StageData> | null>) => {
      return {
        ...state,
        selectedNode: action.payload,
      };
    },
    setConfigPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.isConfigPanelOpen = action.payload;
    },

    // Loading states
    setIsSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // New reducer to manage unsaved changes state
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },

    // Add a new action that updates nodes without marking as unsaved
    setNodesWithoutUnsaved: (
      state,
      action: PayloadAction<Node<StageData>[]>
    ) => {
      return {
        ...state,
        nodes: action.payload,
      };
    },

    // Reset flow
    resetFlow: state => {
      return {
        ...state,
        nodes: initialNodes,
        edges: [],
        selectedNode: null,
        isConfigPanelOpen: false,
        hasUnsavedChanges: false,
      };
    },

    // Bulk update (for loading flow data)
    setFlowData: (
      state,
      action: PayloadAction<{
        nodes: Node<StageData>[];
        edges: Edge[];
      }>
    ) => {
      return {
        ...state,
        nodes: action.payload.nodes,
        edges: action.payload.edges,
        hasUnsavedChanges: false,
      };
    },
  },
});

export const {
  setNodes,
  addNode,
  updateNode,
  removeNode,
  setEdges,
  addEdge,
  removeEdge,
  setSelectedNode,
  setConfigPanelOpen,
  setIsSaving,
  setIsLoading,
  resetFlow,
  setFlowData,
  setHasUnsavedChanges,
  setNodesWithoutUnsaved,
} = callStagesFlowSlice.actions;

export default callStagesFlowSlice.reducer;
